import React, {createRef, ReactElement, RefObject} from 'react';
import MComponent from '../Toolkit/Components/Component';
import {Column} from 'primereact/column';
import MTable, {MTableFilters} from '../Toolkit/Components/Controls/Table';
import MButton from '../Toolkit/Components/Controls/Button';
import MToolbar from '../Toolkit/Components/Controls/Toolbar';
import {MNewIconName} from '../Toolkit/Icons';
import NoteDialog from '../Dialogs/NoteDialog';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';
import TrafficManagementAccessTokenProvider from '../Api/TrafficManagementAccessTokenProvider';
import {CreateNoteRequest, NoteClient, UpdateNoteRequest} from '../Api/NoteClient';
import {IMsalContext} from '@azure/msal-react';
import {Note, NoteMultiReply, NoteReply} from '../Api/ControllerTypes/NoteTypes';
import MSpacer from '../Toolkit/Components/Controls/Spacer';
import EnumContext from '../Context/enum-context';

type NoteRow = {
    data: Note;
}

type Props = {
    parentId: string;
    msalContext: IMsalContext;
}

/** */
type State = {
    filters: MTableFilters;
    rowData: Note[];
    selectedRow?: NoteRow;
    showArchive: boolean;
}

class NoteListControl extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>;

    private readonly m_noteClient: NSwagAdapter<NoteClient>;
    private readonly m_editRef: RefObject<NoteDialog>;

    public constructor(props: Props) {
        super(props);

        this.m_editRef = createRef<NoteDialog>();
        this.m_noteClient = new NSwagAdapter<NoteClient>(new NoteClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));

        this.bindMethods();
        this.initializeState();
    }

    public requestNote(prompt: string, useLastNote: boolean){
        this.m_editRef.current!.show(prompt, useLastNote, this.createComplete);
    }

    public async refresh() {
        await this.loadPageData();
    }

    private bindMethods(): void {
        this.new_Click = this.new_Click.bind(this);
        this.edit_Click = this.edit_Click.bind(this);
        this.delete_Click = this.delete_Click.bind(this);
        this.isPlanner = this.isPlanner.bind(this);
        this.createComplete = this.createComplete.bind(this);
        this.editComplete = this.editComplete.bind(this);
        this.requestNote = this.requestNote.bind(this);
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.toggleArchive_Click = this.toggleArchive_Click.bind(this);
        this.restore_Click = this.restore_Click.bind(this);
        this.isRestoreEnabled = this.isRestoreEnabled.bind(this);
        this.isArchiveEnabled = this.isArchiveEnabled.bind(this);
        this.isEditEnabled = this.isEditEnabled.bind(this);
        this.noteStatusTemplate = this.noteStatusTemplate.bind(this);
    }

    private initializeState(): void {
        this.state = {
            filters: {},
            rowData: [],
            showArchive: false
        };
    }

    async componentDidMount() {
        await this.loadPageData();
    }

    private isEditEnabled(): boolean {
        if(this.state.selectedRow?.data !== undefined) {
            return this.state.selectedRow!.data!.noteStatus === 0;
        }

        return false;
    }

    private isArchiveEnabled(): boolean {
        if(this.state.selectedRow?.data !== undefined) {
            return this.state.selectedRow!.data!.noteStatus === 0;
        }

        return false;
    }

    private isRestoreEnabled(): boolean {
        if(this.state.selectedRow?.data !== undefined) {
            return this.state.selectedRow!.data!.noteStatus === 1;
        }

        return false;
    }

    private isPlanner(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access;
    }

    private new_Click(): void {
        this.m_editRef.current!.show('', false, this.createComplete);
    }

    private edit_Click(): void {
        this.m_editRef.current!.show('', false, this.editComplete, undefined, this.state.selectedRow?.data.noteText );
    }

    private async delete_Click(): Promise<void> {
        const createResult = await this.m_noteClient.callWithReply(
            NoteReply, (client) => client.archive(this.state.selectedRow?.data.id));
        createResult.throwIfFailed();

        await this.loadPageData();
    }

    private async restore_Click(): Promise<void> {
        const restoreReply = await this.m_noteClient.call(
            (client) => client.restore(this.state.selectedRow!.data!.id));

        restoreReply.throwIfFailed();

        await this.loadPageData();
    }

    private async toggleArchive_Click(): Promise<void> {
        this.safeSetState(s => s.showArchive = !s.showArchive);
    }

    private table_onSelectionChange(selection: NoteRow | NoteRow[] | undefined): void {

        this.safeSetState((draft: State) =>
            draft.selectedRow = selection as NoteRow | undefined);

    }

    private async createComplete(newNote: string): Promise<void> {
        const createResult = await this.m_noteClient.callWithReply(
            NoteReply, (client) => client.create(new CreateNoteRequest({
                requestId: this.props.parentId,
                noteText: newNote
            })));
        createResult.throwIfFailed();

        this.safeSetState(draft => draft.rowData.push(createResult.reply!.note!));
    }

    private async editComplete(newNote: string): Promise<void> {
        const editResult = await this.m_noteClient.callWithReply(
            NoteReply, (client) => client.update(new UpdateNoteRequest({
                id: this.state.selectedRow?.data.id,
                noteText: newNote
        })));
        editResult.throwIfFailed();

        await this.loadPageData();
    }

    private createdTemplate(rowData: NoteRow): ReactElement | undefined {
        if(rowData.data.modifiedOn !== null) {
            try {
                const formattedDate = rowData.data.modifiedOn!.getDate().toString().padStart(2, '0') + "/" +
                    (rowData.data.modifiedOn!.getMonth() + 1).toString().padStart(2, '0') + "/" +
                    rowData.data.modifiedOn!.getFullYear() + " " +
                    rowData.data.modifiedOn!.getHours().toString().padStart(2, '0') + ":" +
                    rowData.data.modifiedOn!.getMinutes().toString().padStart(2, '0');
                return <span style={{whiteSpace: 'normal'}}>
                    {formattedDate}
                </span>;
            } catch {
                return undefined;
            }
        }
        return undefined;
    }

    private noteStatusTemplate(rowData: NoteRow): string | null {
        const index = this.context.enums?.noteStatuses.findIndex(value => value.id === rowData.data.noteStatus);

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.noteStatuses[index!].name!;
    }

    private textTemplate(rowData: NoteRow): ReactElement {
        return <p style={{whiteSpace:'pre-wrap'}}>
                    {rowData.data.noteText}
                </p>
    }

    private async loadPageData(): Promise<void> {

        //Read note data.
        const noteResult = await this.m_noteClient.callWithReply(
            NoteMultiReply, (client) => client.getAll(this.props.parentId));
        noteResult.throwIfFailed();

        this.safeSetState(draft => {
            draft.rowData = noteResult.reply!.notes;
        });
    }

    public render(): ReactElement {
        let noteList = this.state.rowData;
        if(!this.state.showArchive){
            noteList = noteList.filter(n => n.noteStatus !== 1);
        }

        return (
            <React.Fragment>
                <NoteDialog ref={this.m_editRef}/>
                <MToolbar>
                    <MButton label="Add Note"
                             color="success"
                             icon={MNewIconName}
                             disabled={false}
                             onClick={this.new_Click}/>
                    <MSpacer/>
                    <MButton label="Archive Note"
                             icon={"pi pi-trash"}
                             color="danger"
                             disabled={!this.isArchiveEnabled() || this.isPlanner()}
                             onClick={this.delete_Click}/>
                    <MSpacer/>
                    <MButton label={this.state.showArchive ? "Hide Archive" : "Show Archive"}
                             icon={this.state.showArchive ? 'pi pi-times' : 'pi pi-check'}
                             color="secondary"
                             disabled={this.isPlanner()}
                             onClick={this.toggleArchive_Click}/>
                    <MSpacer/>
                    {this.state.showArchive && <MButton label="Restore"
                                                        icon={'pi pi-thumbs-up'}
                                                        color="secondary"
                                                        disabled={!this.isRestoreEnabled() || this.isPlanner()}
                                                        onClick={this.restore_Click}/>}
                </MToolbar>
                <MTable
                    rowKey="data.id"
                    rowData={noteList.map(n => ({data: n}))}
                    filters={this.state.filters}
                    onSelectionChange={this.table_onSelectionChange}
                    sortField={'data.modifiedOn'}
                    sortOrder={-1}
                >

                    <Column field="data.modifiedOn"
                            header="Created On"
                            dataType="date"
                            body={this.createdTemplate}
                            filter={false}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column field="data.noteUser"
                            header="Created By"
                            filter={false}
                            sortable={false}
                            style={{width: '15%'}}/>
                    <Column field="data.notesText"
                            header="Text"
                            body={this.textTemplate}
                            filter={false}
                            sortable={false}
                            style={{width: '75%'}}/>
                </MTable>
            </React.Fragment>
        );
    }
}

export default NoteListControl;